import { isArray } from '@ember/array';
import { isNone, isEmpty } from '@ember/utils';
import { assert } from '@ember/debug';

function sum(array) {
  if (isEmpty(array)) {
    return 0;
  }

  return array.reduce((prev, current) => prev + current);
}

export function getPctSeries(numerators, denominators) {
  if (isEmpty(numerators) || isEmpty(denominators)) {
    return;
  }

  // Assume that numerators line up with denominators from beginning. If
  // different lengths, slice from the start.
  if (numerators.length < denominators.length) {
    denominators = denominators.slice(0, numerators.length - 1);
  }

  return denominators.map((denominator, i) => {
    if (denominator === 0) {
      return null;
    }
    return numerators[i] / denominator;
  });
}

export function cumulativeAverageSeries(numerators, denominators) {
  let series;
  let denominatorTotal;
  let numeratorTotal;

  assert('Input numerators and denominators must be non-empty arrays', !(isEmpty(numerators) || isEmpty(denominators)));

  series = [];
  numeratorTotal = 0;
  denominatorTotal = null;

  denominators.forEach(function (denominator, index) {
    numeratorTotal += numerators[index];

    if (denominatorTotal == null) {
      denominatorTotal = denominator;
    } else if (denominator != null) {
      denominatorTotal += denominator;
    }

    if (denominatorTotal === 0 || denominatorTotal == null) {
      return series.push(null);
    } else {
      return series.push(numeratorTotal / denominatorTotal);
    }
  });

  return series;
}

export function avgRate(numerators, denominators, seriesLength) {
  function computeSum(array) {
    if (isArray(array)) {
      if (seriesLength) {
        return sum(array.slice(-seriesLength));
      }

      return sum(array);
    }

    return array;
  }

  let numerator = computeSum(numerators);
  let denominator = computeSum(denominators);

  if (denominator === 0) {
    return 0;
  }

  return numerator / denominator;
}

export function pctChg(oldVal, newVal) {
  if (isNone(oldVal) || isNone(newVal) || oldVal === 0) {
    return null;
  }

  return (newVal - oldVal) / oldVal;
}

function standardError(n, mean) {
  if (n === 0 || mean === 0) {
    return 0;
  }

  return Math.sqrt((mean * (1 - mean)) / n);
}

function getZPercent(z) {
  // z: the number of standard deviations from the mean
  let factK;
  let k;
  let loopStop;
  let sum;
  let term;

  // If z is greater than 6.5 standard deviations from the mean, the number
  // of significant digits will be outside of a reasonable range
  if (z < -6.5) {
    return 0;
  }

  if (z > 6.5) {
    return 1;
  }

  factK = 1;
  sum = 0;
  term = 1;
  k = 0;
  loopStop = Math.exp(-23);

  while (Math.abs(term) > loopStop) {
    term =
      (((0.3989422804 * Math.pow(-1, k) * Math.pow(z, k)) / (2 * k + 1) / Math.pow(2, k)) * Math.pow(z, k + 1)) / factK;
    sum += term;
    k++;
    factK *= k;
  }
  sum += 0.5;

  return sum;
}

export function chanceToBeatControl(aHits, bHits, aN, bN) {
  let aMean;
  let aSe;
  let bMean;
  let bSe;
  let z;

  aMean = aHits / aN;
  bMean = bHits / bN;
  aSe = standardError(aN, aMean);
  bSe = standardError(bN, bMean);
  z = (aMean - bMean) / Math.sqrt(Math.pow(aSe, 2) + Math.pow(bSe, 2));

  return 1 - getZPercent(z);
}
